import { tokenUtils } from '../utils/tokenUtils';

const SESSION_KEY = 'mega_analytics_session_id';
const HEARTBEAT_MS = 30000;

const createSessionId = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `s_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
};

class AnalyticsService {
  constructor() {
    this.apiUrl = process.env.REACT_APP_API_URL;
    this._heartbeatTimer = null;
    this._lastVideoProgressAt = 0;
  }

  getSessionId() {
    let sessionId = sessionStorage.getItem(SESSION_KEY);
    if (!sessionId) {
      sessionId = createSessionId();
      sessionStorage.setItem(SESSION_KEY, sessionId);
    }
    return sessionId;
  }

  getActor() {
    const sessionUser = tokenUtils.getUser() || {};
    let localUser = {};
    try {
      const raw = localStorage.getItem('usuarioConCedula');
      localUser = raw ? JSON.parse(raw) : {};
    } catch {
      localUser = {};
    }

    const email =
      sessionUser.email ||
      sessionUser.Email ||
      localUser.email ||
      localUser.Email ||
      localUser.login ||
      localUser.Login ||
      null;
    const username = sessionUser.user || sessionUser.name || localUser.name || localUser.Name || null;
    const cedula = sessionUser.cedula || sessionUser.Cedula || localUser.cedula || localUser.Cedula || null;
    const login = email || username || cedula || undefined;
    const userId = localUser.id ?? localUser.Id ?? undefined;

    return {
      userId,
      login,
      userName: username || undefined,
      userEmail: email || undefined,
      userCedula: cedula || undefined,
    };
  }

  getAuthHeaders() {
    const token = tokenUtils.get();
    if (!token || tokenUtils.isExpired(token)) return {};
    return { Authorization: `Bearer ${token}` };
  }

  getUtmParams() {
    try {
      const params = new URLSearchParams(window.location.search);
      return {
        source: params.get('utm_source') || undefined,
        medium: params.get('utm_medium') || undefined,
        campaign: params.get('utm_campaign') || undefined,
      };
    } catch {
      return {};
    }
  }

  async _post(path, body, withAuth = false) {
    try {
      const headers = {
        'Content-Type': 'application/json',
        ...(withAuth ? this.getAuthHeaders() : {}),
      };
      const response = await fetch(`${this.apiUrl}${path}`, {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
        keepalive: true,
      });
      if (!response.ok) {
        console.warn(`[analytics] ${path} failed:`, response.status);
        return null;
      }
      return response.json().catch(() => ({}));
    } catch (error) {
      console.warn(`[analytics] ${path} error:`, error.message);
      return null;
    }
  }

  async _get(path) {
    const response = await fetch(`${this.apiUrl}${path}`, {
      headers: {
        'Content-Type': 'application/json',
        ...this.getAuthHeaders(),
      },
    });
    if (!response.ok) {
      throw new Error(`Error consultando analytics (${response.status})`);
    }
    return response.json();
  }

  async trackEvent(eventName, { userId, login, properties } = {}) {
    const actor = this.getActor();
    const resolvedLogin = login || actor.login;
    const resolvedUserId = userId ?? actor.userId;
    const utm = this.getUtmParams();
    return this._post('/Analytics/events', {
      events: [
        {
          eventName,
          userId: resolvedUserId ?? undefined,
          login: resolvedLogin ?? undefined,
          sessionId: this.getSessionId(),
          source: utm.source,
          medium: utm.medium,
          campaign: utm.campaign,
          properties: {
            path: window.location.pathname,
            ...properties,
            login: resolvedLogin || properties?.login,
            userName: properties?.userName || actor.userName,
            userEmail: properties?.userEmail || actor.userEmail,
            userCedula: properties?.userCedula || actor.userCedula,
          },
          occurredAt: new Date().toISOString(),
        },
      ],
    });
  }

  async trackEvents(events) {
    if (!events?.length) return null;
    const actor = this.getActor();
    const utm = this.getUtmParams();
    return this._post('/Analytics/events', {
      events: events.map((e) => {
        const resolvedLogin = e.login || actor.login;
        return {
          eventName: e.eventName,
          userId: e.userId ?? actor.userId,
          login: resolvedLogin ?? undefined,
          sessionId: e.sessionId || this.getSessionId(),
          source: e.source || utm.source,
          medium: e.medium || utm.medium,
          campaign: e.campaign || utm.campaign,
          properties: {
            ...(e.properties || {}),
            login: resolvedLogin || e.properties?.login,
            userName: e.properties?.userName || actor.userName,
            userEmail: e.properties?.userEmail || actor.userEmail,
            userCedula: e.properties?.userCedula || actor.userCedula,
          },
          occurredAt: e.occurredAt || new Date().toISOString(),
        };
      }),
    });
  }

  async heartbeat({ userId, login, endSession = false } = {}) {
    const actor = this.getActor();
    return this._post('/Analytics/sessions/heartbeat', {
      sessionId: this.getSessionId(),
      userId: userId ?? actor.userId ?? undefined,
      login: login || actor.login || undefined,
      endSession,
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
    });
  }

  startSessionTracking(userId) {
    this.heartbeat({ userId });
    if (this._heartbeatTimer) clearInterval(this._heartbeatTimer);
    this._heartbeatTimer = setInterval(() => {
      this.heartbeat({ userId });
    }, HEARTBEAT_MS);

    if (typeof window !== 'undefined' && !this._unloadBound) {
      this._unloadBound = true;
      window.addEventListener('pagehide', () => {
        this.heartbeat({ userId, endSession: true });
      });
    }
  }

  stopSessionTracking(userId) {
    if (this._heartbeatTimer) {
      clearInterval(this._heartbeatTimer);
      this._heartbeatTimer = null;
    }
    return this.heartbeat({ userId, endSession: true });
  }

  async trackVideoProgress({
    userId,
    login,
    videoId,
    courseId,
    positionSeconds,
    durationSeconds,
    eventType = 'heartbeat',
    throttleMs = 10000,
  }) {
    const now = Date.now();
    if (eventType === 'heartbeat' && now - this._lastVideoProgressAt < throttleMs) {
      return null;
    }
    this._lastVideoProgressAt = now;

    const actor = this.getActor();
    return this._post('/Analytics/video-progress', {
      userId: userId ?? actor.userId ?? undefined,
      login: login || actor.login || undefined,
      videoId,
      courseId,
      sessionId: this.getSessionId(),
      positionSeconds,
      durationSeconds,
      eventType,
    });
  }

  async rateCourse({ userId, courseId, rating, comment }) {
    return this._post(
      '/Analytics/course-ratings',
      { userId, courseId, rating, comment },
      true
    );
  }

  // APIs de consulta (para paneles / otro equipo)
  getConversion(from, to) {
    const qs = new URLSearchParams();
    if (from) qs.set('from', from);
    if (to) qs.set('to', to);
    return this._get(`/Analytics/conversion?${qs}`);
  }

  getEngagement(from, to) {
    const qs = new URLSearchParams();
    if (from) qs.set('from', from);
    if (to) qs.set('to', to);
    return this._get(`/Analytics/engagement?${qs}`);
  }

  getContent(from, to, topVideos = 20) {
    const qs = new URLSearchParams({ topVideos: String(topVideos) });
    if (from) qs.set('from', from);
    if (to) qs.set('to', to);
    return this._get(`/Analytics/content?${qs}`);
  }

  getSales(from, to) {
    const qs = new URLSearchParams();
    if (from) qs.set('from', from);
    if (to) qs.set('to', to);
    return this._get(`/Analytics/sales?${qs}`);
  }

  queryEvents({ eventName, from, to, userId, login } = {}) {
    const qs = new URLSearchParams();
    if (eventName) qs.set('eventName', eventName);
    if (from) qs.set('from', from);
    if (to) qs.set('to', to);
    if (userId) qs.set('userId', String(userId));
    if (login) qs.set('login', login);
    const query = qs.toString();
    return this._get(query ? `/Analytics/events?${query}` : '/Analytics/events');
  }

  querySessions({ from, to, userId, login } = {}) {
    const qs = new URLSearchParams();
    if (from) qs.set('from', from);
    if (to) qs.set('to', to);
    if (userId) qs.set('userId', String(userId));
    if (login) qs.set('login', login);
    const query = qs.toString();
    return this._get(query ? `/Analytics/sessions?${query}` : '/Analytics/sessions');
  }
}

const analyticsService = new AnalyticsService();
export default analyticsService;
